import * as React from "react"
import { cn } from "@/lib/utils"
import BumpChart from '@/components/bump-chart'
import axiosInstance from "@/api/axios"
import { useMediaQuery } from "react-responsive"
import { CircleHelp, Download, ChevronDown } from "lucide-react"
import { Tooltip as UITooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { useTranslation } from "react-i18next"

interface Datum {
    x: string;
    y: number;
}

interface ChartData {
    id: string;
    data: Datum[];
}

interface ChartAreaInteractiveProps extends React.HTMLAttributes<HTMLDivElement> {
    className?: string;
    title?: string;
    timeRange?: string;
    bins?: string[];
    customDateRange?: Array<{
        startDate: Date | null;
        endDate: Date | null;
        key: string;
    }>;
    isCustomDateRange?: boolean;
}

export function BinUsageRanking({ 
    className, 
    title, 
    timeRange, 
    bins = [], 
    customDateRange = [{ startDate: null, endDate: null, key: 'selection' }],
    isCustomDateRange = false,
    ...props 
}: ChartAreaInteractiveProps) {
    const { t } = useTranslation();
    const [data, setData] = React.useState<ChartData[]>([])
    const [loading, setLoading] = React.useState<boolean>(false)
    const [error, setError] = React.useState<string | null>(null)
    const exportRef = React.useRef<HTMLDivElement>(null)
    const chartContainerRef = React.useRef<HTMLDivElement>(null)

    const getExportData = React.useCallback(() => {
        if (!data || data.length === 0) return null;

        const allDates = Array.from(new Set(data.flatMap(item => item.data.map(d => d.x)))).sort();

        const headers = [t('binUsageRanking.export.month'), ...data.map(item => `${t('binUsageRanking.export.bin')} ${item.id}`)];
        const rows = allDates.map(date => {
            const rowData = [date];
            data.forEach(item => {
                const matchingData = item.data.find(d => d.x === date);
                rowData.push(matchingData ? matchingData.y.toString() : "0");
            });
            return rowData;
        });

        const chartData = data.map(item => ({
            id: item.id,
            data: item.data.map(d => ({
                x: d.x,
                y: d.y
            }))
        }));

        return {
            title: title || t('binUsageRanking.defaultTitle'),
            headers,
            rows,
            chartData
        };
    }, [data, title, t]);

    const handleExportCSV = () => {
        const exportData = getExportData();
        if (!exportData) return;

        const csvContent = [
            exportData.headers.join(","),
            ...exportData.rows.map(row => row.join(","))
        ].join("\n");

        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.setAttribute("href", url);
        link.setAttribute("download", `bin_usage_ranking_${new Date().toISOString().slice(0, 10)}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleExportPNG = async () => {
        if (!exportRef.current) return;

        const exportControls = exportRef.current.querySelector('.bin-usage-export-controls') as HTMLElement;
        const helpCircle = exportRef.current.querySelector('.help-circle') as HTMLElement;

        try {
            if (exportControls) exportControls.style.display = 'none';
            if (helpCircle) helpCircle.style.display = 'none';

            await new Promise(resolve => setTimeout(resolve, 100));

            const html2canvas = (await import('html2canvas')).default;
            const isDark = document.documentElement.classList.contains('dark');

            const canvas = await html2canvas(exportRef.current, {
                scale: 2,
                logging: false,
                useCORS: true,
                backgroundColor: isDark ? '#0f172a' : '#ffffff',  // captures the element's actual bg
                windowWidth: exportRef.current.scrollWidth,
                windowHeight: exportRef.current.scrollHeight,
            });

            const link = document.createElement('a');
            link.download = `bin_usage_ranking_${new Date().toISOString().slice(0, 10)}.png`;
            link.href = canvas.toDataURL('image/png');
            link.click();
        } catch (err) {
            console.error('Failed to export PNG:', err);
        } finally {
            if (exportControls) exportControls.style.display = '';
            if (helpCircle) helpCircle.style.display = '';
        }
    };

    React.useEffect(() => {
        setData([])
        setError(null)

        if (!bins || bins.length === 0) {
            console.log("Missing required bins parameter, skipping data fetch");
            return;
        }

        // Check if we should use custom date range or timeRange
        if (!isCustomDateRange && !timeRange) {
            console.log("Missing timeRange parameter and custom date range not active, skipping data fetch");
            return;
        }

        setLoading(true)

        const params: any = {
            bins: bins.join(",")
        };

        // Add custom date range if active - this takes priority over timeRange
        if (isCustomDateRange && customDateRange[0] && customDateRange[0].startDate && customDateRange[0].endDate) {
            const startDate = customDateRange[0].startDate;
            const endDate = customDateRange[0].endDate;
            
            // Convert to ISO string - handle Date objects, strings, and other formats
            if (startDate instanceof Date) {
                params.start_date = startDate.toISOString();
            } else if (typeof startDate === 'string') {
                params.start_date = startDate;
            } else {
                params.start_date = new Date(startDate).toISOString();
            }
            
            if (endDate instanceof Date) {
                params.end_date = endDate.toISOString();
            } else if (typeof endDate === 'string') {
                params.end_date = endDate;
            } else {
                params.end_date = new Date(endDate).toISOString();
            }
            
            console.log('BinUsageRanking: Using custom date range', params.start_date, params.end_date);
        } else {
            // Only use timeRange if NOT using custom date range
            params.timeRange = timeRange;
            console.log('BinUsageRanking: Using time range', timeRange);
        }

        console.log("BinUsageRanking: Fetching data with params:", params);

        axiosInstance
            .get("/bin_usage", {
                params: params,
                paramsSerializer: {
                    indexes: null,
                }
            })
            .then((response) => {
                let chartData: ChartData[];

                if (Array.isArray(response.data)) {
                    chartData = response.data;
                }
                else {
                    setError(t('binUsageRanking.errors.invalidDataFormat'));
                    chartData = [];
                }

                setData(chartData);
            })
            .catch((error) => {
                console.error("Error fetching bin usage data:", error);
                setError(error.message || t('binUsageRanking.errors.failedToLoadData'));
                setData([]);
            })
            .finally(() => {
                setLoading(false);
            });
    }, [timeRange, bins, customDateRange, isCustomDateRange, t]);

    const exportData = getExportData();

    return (
        <div
            ref={exportRef}
            className={cn("lg:col-span-2 aspect-[1/1] sm:aspect-[16/10] sm:rounded-lg bg-muted/50 p-4 exportable-section", className)}
            data-export-data={JSON.stringify(exportData)}
            {...props}
        >
            <div className="relative">
                <div className="flex justify-between items-center pb-1">
                    <div>
                        <h4 className="scroll-m-20 text-xl font-semibold tracking-tight">
                            {title || t('binUsageRanking.defaultTitle')}
                        </h4>
                        {isCustomDateRange && customDateRange[0]?.startDate && customDateRange[0]?.endDate && (
                            <p className="text-sm text-muted-foreground mt-1">
                                {new Date(customDateRange[0].startDate).toLocaleDateString()} - {new Date(customDateRange[0].endDate).toLocaleDateString()}
                            </p>
                        )}
                    </div>
                    <div className="flex items-center space-x-1 bin-usage-export-controls">
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-muted-foreground hover:text-primary"
                                    disabled={!exportData || loading}
                                >
                                    <Download className="h-4 w-4 mr-1" />
                                    {t('binUsageRanking.export.export')}
                                    <ChevronDown className="h-4 w-4 ml-1" />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                                <DropdownMenuItem
                                    onClick={handleExportCSV}
                                    disabled={!exportData || loading}
                                >
                                    {t('binUsageRanking.export.downloadCSV')}
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                    onClick={handleExportPNG}
                                    disabled={!exportData || loading}
                                >
                                    {t('binUsageRanking.export.downloadPNG')}
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                        <TooltipProvider>
                            <UITooltip>
                                <TooltipTrigger asChild>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        className="text-muted-foreground hover:text-primary h-8 w-8 p-0 help-circle"
                                    >
                                        <CircleHelp className="h-4 w-4" />
                                    </Button>
                                </TooltipTrigger>
                                <TooltipContent side="left">
                                    <p dangerouslySetInnerHTML={{
                                        __html: t('binUsageRanking.help.description') + '<br/><br/><strong>Date Range:</strong> Use the calendar in the header to select a custom date range'
                                    }} />
                                </TooltipContent>
                            </UITooltip>
                        </TooltipProvider>
                    </div>
                </div>
            </div>

            <div
                ref={chartContainerRef}
                className="px-1 pt-2 sm:px-2 sm:pt-2"
                style={{width: '100%', height: '100%'}}
            >
                {loading ? (
                    // Skeleton — two lines swapping rank 1 / 2 with smooth
                    // S-curves (a bump chart's defining shape), placeholder
                    // rank labels on the left, bin labels on the right, and
                    // day ticks underneath. Loading state only, never real data.
                    // Rank labels sit at 25% / 75% height to match the y=10 and
                    // y=30 lines in the 40-unit viewBox; update both together.
                    <div className="flex flex-col h-full w-full motion-safe:animate-pulse" aria-busy="true">
                        <span className="sr-only" role="status">{t('binUsageRanking.loading', 'Loading…')}</span>

                        <div className="flex-1 min-h-0 flex">
                            {/* left: rank labels (1, 2) */}
                            <div className="relative w-8 shrink-0" aria-hidden="true">
                                <div className="absolute left-2 top-[25%] -translate-y-1/2 h-2 w-2 rounded bg-gray-200 dark:bg-slate-700" />
                                <div className="absolute left-2 top-[75%] -translate-y-1/2 h-2 w-2 rounded bg-gray-200 dark:bg-slate-700" />
                            </div>

                            {/* chart: two lines swapping rank 1 / 2 */}
                            <div className="flex-1 border-l border-b border-gray-200 dark:border-slate-700">
                                <svg viewBox="0 0 100 40" preserveAspectRatio="none" className="w-full h-full" aria-hidden="true">
                                    {[
                                        "M0,10 L8,10 C11,10 11,30 14,30 C17,30 17,10 20,10 L62,10 C65,10 65,30 68,30 C71,30 71,10 74,10 L100,10",
                                        "M0,30 L8,30 C11,30 11,10 14,10 C17,10 17,30 20,30 L62,30 C65,30 65,10 68,10 C71,10 71,30 74,30 L100,30",
                                    ].map((d, i) => (
                                        <path
                                            key={i}
                                            d={d}
                                            fill="none"
                                            className="stroke-gray-300 dark:stroke-slate-600"
                                            strokeWidth="2.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            vectorEffect="non-scaling-stroke"
                                        />
                                    ))}
                                </svg>
                            </div>

                            {/* right: bin labels (T10, B1) */}
                            <div className="relative w-12 shrink-0" aria-hidden="true">
                                <div className="absolute left-2 top-[25%] -translate-y-1/2 h-2 w-6 rounded bg-gray-200 dark:bg-slate-700" />
                                <div className="absolute left-2 top-[75%] -translate-y-1/2 h-2 w-6 rounded bg-gray-200 dark:bg-slate-700" />
                            </div>
                        </div>

                        {/* x-axis day ticks, inset to line up with the chart area */}
                        <div className="flex justify-between pt-2 pl-8 pr-12" aria-hidden="true">
                            {[0, 1, 2, 3, 4, 5, 6].map((i) => (
                                <div key={i} className="h-2 w-6 rounded bg-gray-200 dark:bg-slate-700" />
                            ))}
                        </div>
                    </div>
                ) : error ? (
                    <div className="flex flex-col items-center justify-center h-[400px]">
                        <div className="text-muted-foreground text-center">
                            <p className="mb-2 text-lg">{t('binUsageRanking.states.errorLoadingData')}</p>
                            <p className="text-sm">{error}</p>
                        </div>
                    </div>
                ) : !exportData ? (
                    <div className="flex flex-col items-center justify-center h-[400px]">
                        <div className="text-muted-foreground text-center">
                            <p className="mb-2 text-lg">{t('binUsageRanking.states.noDataAvailable')}</p>
                            <p className="text-sm">{t('binUsageRanking.states.noRankingData')}</p>
                        </div>
                    </div>
                ) : (
                    <div className="w-full h-full">
                        <BumpChart
                            data={exportData.chartData}
                        />
                    </div>
                )}
            </div>
        </div>
    )
}